import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
} from '@hello-pangea/dnd';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { COURSE_STATE, ENROLLMENT_KIND } from '@theailab-dev/parchment';
import type { EnrollmentKind } from '@theailab-dev/parchment';
import DOMPurify from 'dompurify';
import React, { useState, useMemo, useCallback } from 'react';
import { HiTrash, HiPencil, HiPlus } from 'react-icons/hi';
import { MdDragIndicator } from 'react-icons/md';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { deleteChapter, createChapter, updateChapter } from '../../api/chapterApi';
import { reorderChapters, reorderLessons, deleteCourse } from '../../api/courseApi';
import { deleteLesson } from '../../api/lessonApi';
import { DetailPageLayout } from '../../components/Layout/DetailPageLayout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import ChapterEditModal from '../../components/ui/ChapterEditModal';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { DateRangeFilter } from '../../components/ui/DateRangeFilter';
import { InfoItem } from '../../components/ui/InfoItem';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { SelectFilter } from '../../components/ui/SelectFilter';
import { useCourseDetail } from '../../hooks/useCourseDetail';
import { useCourseEnrollments } from '../../hooks/useCourseEnrollments';
import { formatDateTime } from '../../utils/dateTime';
import { getMaterialDisplayName } from '../../utils/materialUrl';

/**
 * 이미지 URL을 절대 경로로 변환하는 헬퍼 함수
 * 상대 경로인 경우 API base URL을 붙임
 */
const getImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;

  // 이미 절대 경로인 경우 그대로 반환
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // 상대 경로인 경우 API base URL을 붙임
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4010';
  // URL이 /로 시작하지 않으면 /를 추가
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_URL}${normalizedUrl}`;
};

/**
 * CourseDetail 컴포넌트 - 코스 상세 정보 및 커리큘럼 관리
 */
const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: course, isLoading, error } = useCourseDetail(parseInt(id || '0', 10));

  // 강의 데이터가 변경될 때 이미지 에러 상태 초기화
  React.useEffect(() => {
    setInstructorImageError(false);
    // 디버깅: 강사 이미지 URL 확인
    if (course?.instructor?.imageUrl) {
      console.log('강사 프로필 이미지 URL:', {
        imageUrl: course.instructor.imageUrl,
        isAbsolute: course.instructor.imageUrl.startsWith('http'),
        instructorName: course.instructor.name,
      });
    }
  }, [course?.instructor?.imageUrl, course?.instructor?.name]);

  // 챕터/레슨 삭제 관련 상태
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    isAlert?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    isAlert: false,
  });

  // 챕터 수정/추가 모달 상태
  const [editChapterTarget, setEditChapterTarget] = useState<{
    isOpen: boolean;
    chapterId?: number;
    initialTitle?: string;
  }>({
    isOpen: false,
  });

  // 순서 변경 관련 상태
  const [isReordering, setIsReordering] = useState(false);
  // 임시 순서 변경 상태 (드래그로 변경된 순서를 저장)
  const [pendingChapterOrder, setPendingChapterOrder] = useState<number[] | null>(null);
  const [pendingLessonOrders, setPendingLessonOrders] = useState<Record<number, number[]>>({});
  // 강사 이미지 로드 에러 상태
  const [instructorImageError, setInstructorImageError] = useState(false);

  // 수강자 목록 페이지네이션 state
  const [enrollmentsPage, setEnrollmentsPage] = useState(1);
  const enrollmentsLimit = 10;

  // 수강자 목록 필터 - URL 파라미터에서 직접 가져오기
  const filterKind = searchParams.get('filterKind') || '';
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';

  // 타입 안전한 filterKind 값
  const typedFilterKind: EnrollmentKind | undefined = Object.values(ENROLLMENT_KIND).includes(
    filterKind as EnrollmentKind,
  )
    ? (filterKind as EnrollmentKind)
    : undefined;

  // 수강자 목록 조회
  const { data: enrollmentsData, isLoading: isLoadingEnrollments } = useCourseEnrollments(parseInt(id || '0', 10), {
    page: enrollmentsPage,
    limit: enrollmentsLimit,
    filterKind: typedFilterKind,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  // 수강자 필터 핸들러
  const handleFilterKindChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (value) {
      params.set('filterKind', value);
    } else {
      params.delete('filterKind');
    }
    setSearchParams(params);
  };

  const handleDateChange = (type: 'startDate' | 'endDate', value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (value) {
      params.set(type, value);
    } else {
      params.delete(type);
    }
    setSearchParams(params);
  };

  // 삭제 뮤테이션
  const deleteMutation = useMutation({
    mutationFn: async ({ type, targetId }: { type: 'chapter' | 'lesson'; targetId: number; chapterId?: number }) => {
      if (!id) throw new Error('강의 ID가 없습니다.');
      const courseId = parseInt(id, 10);

      if (type === 'chapter') {
        await deleteChapter(courseId, targetId);
        // 백엔드에서 자동으로 순서 재정렬 처리
      } else if (type === 'lesson') {
        await deleteLesson(targetId);
        // 백엔드에서 자동으로 순서 재정렬 처리
      } else {
        throw new Error('삭제할 항목의 정보가 불충분합니다.');
      }
    },
    onSuccess: async () => {
      if (!id) return;
      const courseId = parseInt(id, 10);
      // 쿼리 캐시 무효화 (현재 페이지에서 useCourseDetail이 활성화되어 있으면 자동 refetch됨)
      await queryClient.invalidateQueries({ queryKey: ['course', courseId] });
      setConfirmModal({ ...confirmModal, isOpen: false });
    },
    onError: (err: unknown) => {
      console.error('삭제 실패:', err);
      const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.';
      alert('삭제 중 오류가 발생했습니다: ' + errorMessage);
    },
  });

  /**
   * 챕터 추가 버튼 클릭
   */
  const handleAddChapter = () => {
    setEditChapterTarget({
      isOpen: true,
      chapterId: undefined,
      initialTitle: '',
    });
  };

  /**
   * 챕터 수정 버튼 클릭
   */
  const handleEditChapter = (chapterId: number, currentTitle: string) => {
    setEditChapterTarget({
      isOpen: true,
      chapterId,
      initialTitle: currentTitle,
    });
  };

  /**
   * 삭제 버튼 클릭
   */
  const handleDeleteClick = (
    type: 'course' | 'chapter' | 'lesson',
    targetId: number,
    title: string,
    chapterId?: number,
  ) => {
    let message = `정말로 '${title}'을(를) 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`;

    if (type === 'course' && course) {
      // 강의 삭제 시: 하위 챕터와 레슨 개수 표시
      const chapterCount = course.chapters?.length || 0;
      const totalLessonCount = course.chapters?.reduce((sum, ch) => sum + (ch.lessons?.length || 0), 0) || 0;
      message = `정말로 '${title}' 강의를 삭제하시겠습니까?\n\n경고: 이 강의에 포함된 ${chapterCount}개의 챕터와 ${totalLessonCount}개의 레슨이 모두 삭제됩니다.\n이 작업은 되돌릴 수 없습니다.`;
    } else if (type === 'chapter' && course) {
      // 챕터 삭제 시: 하위 레슨 개수 표시
      const chapter = course.chapters?.find((ch) => ch.id === targetId);
      const lessonCount = chapter?.lessons?.length || 0;
      message = `정말로 '${title}' 챕터를 삭제하시겠습니까?\n\n경고: 이 챕터에 포함된 ${lessonCount}개의 레슨이 모두 삭제됩니다.\n이 작업은 되돌릴 수 없습니다.`;
    }

    setConfirmModal({
      isOpen: true,
      isAlert: false,
      title: type === 'course' ? '강의 삭제' : type === 'chapter' ? '챕터 삭제' : '레슨 삭제',
      message,
      onConfirm: () => {
        if (type === 'course') {
          handleDeleteCourse(targetId);
        } else {
          deleteMutation.mutate({ type, targetId, chapterId });
        }
      },
    });
  };

  /**
   * 강의 삭제 핸들러
   */
  const handleDeleteCourse = async (courseId: number) => {
    try {
      await deleteCourse(courseId);
      // 강의 목록으로 이동
      navigate('/dashboard/courses');
    } catch (error: unknown) {
      console.error('강의 삭제 실패:', error);
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as { message?: string })?.message ||
        '강의 삭제에 실패했습니다.';

      setConfirmModal({
        isOpen: true,
        title: '삭제 실패',
        message: errorMessage,
        onConfirm: () => setConfirmModal((prev) => ({ ...prev, isOpen: false })),
        isAlert: true,
      });
    }
  };

  /**
   * 드래그 앤 드롭 종료 핸들러
   */
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      // 순서 변경 모드가 아니면 무시
      if (!isReordering) return;

      if (!result.destination || !course || !id || !course.chapters) return;

      const { source, destination, type } = result;

      // 챕터 순서 변경 (로컬 상태만 업데이트)
      if (type === 'CHAPTER') {
        if (source.index === destination.index) return;

        // 현재 표시된 순서를 기준으로 변경 (pendingChapterOrder가 있으면 그것을 사용)
        const currentOrder = pendingChapterOrder || course.chapters.map((c) => c.id);
        const newOrder = Array.from(currentOrder);
        const [reorderedId] = newOrder.splice(source.index, 1);
        if (reorderedId !== undefined) {
          newOrder.splice(destination.index, 0, reorderedId);
        }

        // 임시 순서 저장
        setPendingChapterOrder(newOrder);
      }

      // 레슨 순서 변경 (로컬 상태만 업데이트)
      if (type === 'LESSON') {
        // 다른 챕터로 이동 불가
        if (source.droppableId !== destination.droppableId) {
          alert('레슨은 같은 챕터 내에서만 순서를 변경할 수 있습니다.');
          return;
        }

        // 같은 위치면 변경 불필요
        if (source.index === destination.index) {
          return;
        }

        const chapterIdStr = source.droppableId.replace('chapter-', '');
        const chapterId = parseInt(chapterIdStr, 10);

        // 현재 표시된 챕터를 찾기 (pendingChapterOrder가 있으면 정렬된 배열에서 찾아야 함)
        let sortedChapters = [...course.chapters];

        if (pendingChapterOrder && pendingChapterOrder.length > 0) {
          const chapterMap = new Map(sortedChapters.map((c) => [c.id, c]));
          sortedChapters = pendingChapterOrder
            .map((id) => chapterMap.get(id))
            .filter((c): c is NonNullable<typeof c> => c !== undefined);
        }

        const chapter = sortedChapters.find((c) => c?.id === chapterId);

        if (!chapter) return;

        // 현재 표시된 레슨 순서를 기준으로 변경
        // pendingLessonOrders가 있으면 그것을 사용하되, chapter.lessons에 실제로 존재하는 레슨만 포함
        const validLessonIdSet = new Set(chapter.lessons.map((l) => l.id));
        let currentLessonOrder: number[];

        if (pendingLessonOrders[chapterId]) {
          // pendingLessonOrders에서 유효한 레슨만 필터링
          const validPendingLessonIds = pendingLessonOrders[chapterId].filter((id) => validLessonIdSet.has(id));
          // pendingLessonIds에 없는 레슨은 원래 순서대로 뒤에 추가
          const allLessonIds = chapter.lessons.map((l) => l.id);
          const remainingLessonIds = allLessonIds.filter((id) => !validPendingLessonIds.includes(id));
          currentLessonOrder = [...validPendingLessonIds, ...remainingLessonIds];
        } else {
          currentLessonOrder = chapter.lessons.map((l) => l.id);
        }

        // 유효한 레슨만 포함된 순서로 재정렬
        const newLessonOrder = Array.from(currentLessonOrder);
        const [reorderedLessonId] = newLessonOrder.splice(source.index, 1);
        if (reorderedLessonId !== undefined) {
          newLessonOrder.splice(destination.index, 0, reorderedLessonId);
        }

        // 임시 순서 저장 (해당 챕터에 속하는 레슨만 포함)
        setPendingLessonOrders((prev) => ({
          ...prev,
          [chapterId]: newLessonOrder,
        }));
      }
    },
    [isReordering, course, id, pendingChapterOrder, pendingLessonOrders],
  );

  /**
   * 순서 변경 완료 핸들러
   */
  const handleCompleteReordering = async () => {
    if (!course || !id) return;
    const courseId = parseInt(id, 10);

    try {
      // 원래 챕터 순서
      const originalChapterOrder = course.chapters.map((c) => c.id);

      // 챕터 순서 변경 확인
      const hasChapterOrderChanged = pendingChapterOrder
        ? JSON.stringify(originalChapterOrder) !== JSON.stringify(pendingChapterOrder)
        : false;

      // 레슨 순서 변경 확인
      const lessonOrderChanges: Array<{
        chapterId: number;
        lessonIds: number[];
      }> = [];

      // 모든 챕터의 모든 레슨을 Map으로 만들어서 빠른 조회 가능하도록 함
      const lessonToChapterMap = new Map<number, number>();
      course.chapters.forEach((chapter) => {
        chapter.lessons.forEach((lesson) => {
          lessonToChapterMap.set(lesson.id, chapter.id);
        });
      });

      // 디버깅: 모든 챕터와 레슨 정보 출력
      console.log('🔍 전체 챕터 및 레슨 정보:', {
        chapters: course.chapters.map((ch) => ({
          id: ch.id,
          title: ch.title,
          lessonIds: ch.lessons.map((l) => l.id),
          lessonCount: ch.lessons.length,
        })),
        lessonToChapterMap: Array.from(lessonToChapterMap.entries()),
      });

      if (Object.keys(pendingLessonOrders).length > 0) {
        for (const [chapterIdStr, pendingLessonIds] of Object.entries(pendingLessonOrders)) {
          const chapterId = parseInt(chapterIdStr, 10);
          const chapter = course.chapters.find((c) => c.id === chapterId);

          if (chapter) {
            // 해당 챕터에 실제로 속하는 레슨 ID만 필터링
            // lessonToChapterMap을 사용하여 각 레슨이 실제로 해당 챕터에 속하는지 확인
            const validPendingLessonIds = pendingLessonIds.filter((id) => {
              const actualChapterId = lessonToChapterMap.get(id);
              if (actualChapterId !== chapterId) {
                console.warn(`레슨 ${id}는 챕터 ${chapterId}에 속하지 않습니다. 실제 챕터: ${actualChapterId}`);
                return false;
              }
              return true;
            });

            // 챕터의 모든 레슨을 포함하되, validPendingLessonIds의 순서를 우선 적용
            const allLessonIds = chapter.lessons.map((l) => l.id);
            const remainingLessonIds = allLessonIds.filter((id) => !validPendingLessonIds.includes(id));
            const finalLessonIds = [...validPendingLessonIds, ...remainingLessonIds];

            console.log('레슨 순서 변경 검증:', {
              chapterId,
              chapterTitle: chapter.title,
              chapterLessonIds: chapter.lessons.map((l) => l.id),
              chapterLessons: chapter.lessons.map((l) => ({
                id: l.id,
                title: l.title,
              })),
              pendingLessonIds,
              validPendingLessonIds,
              remainingLessonIds,
              finalLessonIds,
              invalidLessonIds: pendingLessonIds.filter((id) => !validPendingLessonIds.includes(id)),
              lessonToChapterMapEntries: pendingLessonIds.map((id) => ({
                lessonId: id,
                actualChapterId: lessonToChapterMap.get(id),
                expectedChapterId: chapterId,
                isValid: lessonToChapterMap.get(id) === chapterId,
              })),
            });

            const originalLessonOrder = chapter.lessons.map((l) => l.id);
            const hasChanged = JSON.stringify(originalLessonOrder) !== JSON.stringify(finalLessonIds);

            if (hasChanged) {
              lessonOrderChanges.push({
                chapterId,
                lessonIds: finalLessonIds,
              });
            }
          }
        }
      }

      // 변경된 것이 없으면 API 호출하지 않음
      if (!hasChapterOrderChanged && lessonOrderChanges.length === 0) {
        // 상태 초기화만 하고 종료
        setPendingChapterOrder(null);
        setPendingLessonOrders({});
        setIsReordering(false);
        return;
      }

      // 챕터 순서 변경 API 호출
      if (hasChapterOrderChanged && pendingChapterOrder) {
        await reorderChapters(courseId, pendingChapterOrder);
      }

      // 레슨 순서 변경 API 호출 (변경된 것만)
      if (lessonOrderChanges.length > 0) {
        const lessonOrderPromises = lessonOrderChanges.map(({ chapterId, lessonIds }) => {
          // 최종 검증: 각 레슨이 실제로 해당 챕터에 속하는지 확인
          const chapter = course.chapters.find((c) => c.id === chapterId);
          if (!chapter) {
            console.error(`챕터 ${chapterId}를 찾을 수 없습니다.`);
            return Promise.resolve([]);
          }

          const chapterLessonIds = new Set(chapter.lessons.map((l) => l.id));
          const invalidLessonIds = lessonIds.filter((id) => !chapterLessonIds.has(id));

          if (invalidLessonIds.length > 0) {
            console.error(`챕터 ${chapterId}에 속하지 않는 레슨 ID 발견:`, invalidLessonIds);
            console.error(`챕터 ${chapterId}의 실제 레슨 ID:`, Array.from(chapterLessonIds));
            console.error(`전송하려는 레슨 ID:`, lessonIds);
            // 잘못된 레슨 ID를 제외하고 전송
            const validLessonIds = lessonIds.filter((id) => chapterLessonIds.has(id));
            console.warn(`잘못된 레슨 ID를 제외하고 전송:`, validLessonIds);
            lessonIds = validLessonIds;
          }

          console.log('레슨 순서 변경 요청:', {
            chapterId,
            chapterTitle: chapter.title,
            lessonIds,
            lessonCount: lessonIds.length,
            chapterLessonCount: chapter.lessons.length,
          });

          return reorderLessons(chapterId, lessonIds);
        });
        await Promise.all(lessonOrderPromises);
      }

      // 상태 초기화
      setPendingChapterOrder(null);
      setPendingLessonOrders({});
      setIsReordering(false);

      // 쿼리 캐시 무효화 (현재 페이지에서 useCourseDetail이 활성화되어 있으면 자동 refetch됨)
      await queryClient.invalidateQueries({ queryKey: ['course', courseId] });
    } catch (error) {
      console.error('순서 변경 실패:', error);
      alert('순서 변경에 실패했습니다.');
      // 실패 시 원래 데이터로 복원 (에러 발생 시 원래 데이터는 캐시에 있으므로 refetch 불필요)
      // 상태만 초기화
      setPendingChapterOrder(null);
      setPendingLessonOrders({});
      setIsReordering(false);
    }
  };

  /**
   * 순서 변경 취소 핸들러
   */
  const handleCancelReordering = () => {
    // 임시 상태 초기화
    setPendingChapterOrder(null);
    setPendingLessonOrders({});
    setIsReordering(false);
    // 취소 시에는 로컬 상태만 초기화하고 서버 요청은 하지 않음
    // (원래 데이터는 이미 course 상태에 있으므로 refetch 불필요)
  };

  /**
   * 시간 포맷팅 (HH:MM:SS 또는 MM:SS)
   */
  const formatDuration = (duration: string | null | undefined) => {
    if (!duration) return '-';
    // duration이 이미 포맷된 문자열이면 그대로 반환
    if (duration.includes(':')) return duration;
    // 초 단위로 변환
    const seconds = parseInt(duration, 10);
    if (isNaN(seconds)) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  };

  // chapters를 메모이제이션하여 불필요한 리렌더링 방지
  // pendingChapterOrder와 pendingLessonOrders를 반영하여 정렬
  const chapters = useMemo(() => {
    if (!course?.chapters) return [];

    let sortedChapters = [...course.chapters];

    // 챕터 순서 변경이 있으면 반영
    if (pendingChapterOrder && pendingChapterOrder.length > 0) {
      const chapterMap = new Map(sortedChapters.map((c) => [c.id, c]));
      sortedChapters = pendingChapterOrder
        .map((id) => chapterMap.get(id))
        .filter((c): c is NonNullable<typeof c> => c !== undefined);
    }

    // 레슨 순서 변경이 있으면 반영
    // 각 챕터의 레슨은 해당 챕터에 속하는 레슨만 포함
    sortedChapters = sortedChapters.map((chapter) => {
      const pendingLessonIds = pendingLessonOrders[chapter.id];
      if (pendingLessonIds && pendingLessonIds.length > 0) {
        // 해당 챕터에 실제로 속하는 레슨만 필터링
        const validLessonIdSet = new Set(chapter.lessons.map((l) => l.id));
        const validPendingLessonIds = pendingLessonIds.filter((id) => validLessonIdSet.has(id));

        // 유효한 레슨만 포함하여 정렬
        const lessonMap = new Map(chapter.lessons.map((l) => [l.id, l]));
        const sortedLessons = validPendingLessonIds
          .map((id) => lessonMap.get(id))
          .filter((l): l is NonNullable<typeof l> => l !== undefined);

        // pendingLessonIds에 없는 레슨은 원래 순서대로 뒤에 추가
        const remainingLessons = chapter.lessons.filter((l) => !validPendingLessonIds.includes(l.id));

        return { ...chapter, lessons: [...sortedLessons, ...remainingLessons] };
      }
      return chapter;
    });

    return sortedChapters;
  }, [course?.chapters, pendingChapterOrder, pendingLessonOrders]);

  if (!id) return null;

  return (
    <DetailPageLayout
      title="강의 상세"
      subtitle="강의 정보와 커리큘럼을 관리합니다."
      isLoading={isLoading}
      isError={!!error}
      errorMessage={error?.message}
      disableScroll={isReordering}
      actions={
        <div className="flex items-center gap-3">
          <Button
            variant="danger"
            onClick={() => handleDeleteClick('course', parseInt(id || '0', 10), course?.title || '')}>
            강의 삭제
          </Button>
          <Button variant="primary" onClick={() => navigate(`/dashboard/courses/${id}/edit`)}>
            강의 수정
          </Button>
        </div>
      }>
      {course && (
        <>
          {/* 삭제 확인 모달 */}
          <ConfirmModal
            isOpen={confirmModal.isOpen}
            onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            title={confirmModal.title}
            message={confirmModal.message}
            type="warning"
            buttons={
              confirmModal.isAlert
                ? [
                    {
                      label: '확인',
                      onClick: () => setConfirmModal({ ...confirmModal, isOpen: false }),
                      variant: 'primary',
                    },
                  ]
                : [
                    {
                      label: '취소',
                      onClick: () => setConfirmModal({ ...confirmModal, isOpen: false }),
                      variant: 'secondary',
                    },
                    {
                      label: '삭제',
                      onClick: confirmModal.onConfirm,
                      variant: 'danger',
                      isLoading: deleteMutation.isPending,
                    },
                  ]
            }
          />

          {/* 챕터 수정/추가 모달 */}
          {editChapterTarget.isOpen && (
            <ChapterEditModal
              isOpen={editChapterTarget.isOpen}
              onClose={() => {
                setEditChapterTarget({ ...editChapterTarget, isOpen: false });
              }}
              chapterId={editChapterTarget.chapterId}
              chapterTitle={editChapterTarget.initialTitle}
              maxSeq={course?.chapters.length ? Math.max(...course.chapters.map((c) => c.seq)) : 0}
              onSave={async (data) => {
                try {
                  const courseId = parseInt(id || '0', 10);

                  if (editChapterTarget.chapterId) {
                    // 수정: seq는 전달하지 않음 (드래그로 순서 변경)
                    await updateChapter(courseId, editChapterTarget.chapterId, {
                      title: data.title,
                    });
                  } else {
                    // 생성: seq는 자동 계산된 값 사용
                    await createChapter(courseId, {
                      title: data.title,
                      seq: data.seq!,
                    });
                  }

                  // 쿼리 캐시 무효화 (현재 페이지에서 useCourseDetail이 활성화되어 있으면 자동 refetch됨)
                  await queryClient.invalidateQueries({
                    queryKey: ['course', courseId],
                  });
                  setEditChapterTarget({ ...editChapterTarget, isOpen: false });
                } catch (error) {
                  console.error('챕터 저장 실패:', error);
                  alert('챕터 저장 중 오류가 발생했습니다.');
                }
              }}
            />
          )}

          <div className="space-y-6">
            {/* 기본 정보 카드 */}
            <Card title="기본 정보">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <InfoItem label="강의명" value={course.title} />
                <InfoItem
                  label="상태"
                  value={
                    <Badge variant={course.state === COURSE_STATE.NORMAL ? 'success' : 'neutral'}>{course.state}</Badge>
                  }
                />
                <InfoItem label="종류" value={course.kind} />
                <InfoItem label="레벨" value={course.level} />
                <InfoItem
                  label="유료 특강 여부"
                  value={
                    <Badge variant={course.isSpecial ? 'purple' : 'neutral'}>
                      {course.isSpecial ? '유료 특강' : '일반 강의'}
                    </Badge>
                  }
                />
                <InfoItem label="총 레슨 수" value={course.totalLessonCount ? `${course.totalLessonCount}개` : '0개'} />
                <InfoItem label="원가" value={course.price ? `${course.price.toLocaleString()}원` : '-'} />
                <InfoItem label="총 강의 시간" value={formatDuration(course.totalDuration)} />
                <InfoItem label="생성일" value={new Date(course.createdAt).toLocaleDateString()} />
                <InfoItem label="수정일" value={new Date(course.updatedAt).toLocaleDateString()} />
                <InfoItem
                  label="강의 자료"
                  value={
                    (course as { materialsUrl?: string | null }).materialsUrl ? (
                      <a
                        href={(course as { materialsUrl: string }).materialsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block max-w-full truncate text-indigo-600 hover:underline"
                        title={(course as { materialsUrl: string }).materialsUrl}>
                        {getMaterialDisplayName((course as { materialsUrl: string }).materialsUrl)}
                      </a>
                    ) : (
                      '-'
                    )
                  }
                />
              </div>
              {(course.startAt || course.endAt || course.periodDays !== null) && (
                <div className="mt-6 border-t border-slate-100 pt-6">
                  <h4 className="mb-4 text-sm font-semibold tracking-wide text-slate-600 uppercase">강의 기간</h4>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <InfoItem label="시작 일시" value={formatDateTime(course.startAt)} />
                    <InfoItem label="종료 일시" value={formatDateTime(course.endAt)} />
                    <InfoItem
                      label="수강권 유효 기간"
                      value={course.periodDays !== null ? `${course.periodDays}일` : '-'}
                    />
                  </div>
                  {course.endAt && course.periodDays !== null && (
                    <span className="mt-3 block text-xs text-amber-600">
                      종료 일시가 설정되어 있어 수강권 유효 기간은 현재 만료일 계산에서 무시됩니다.
                    </span>
                  )}
                </div>
              )}
              {course.preknowledge && (
                <div className="mt-6 border-t border-slate-100 pt-6">
                  <InfoItem
                    label="사전지식"
                    value={
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(course.preknowledge),
                        }}
                      />
                    }
                  />
                </div>
              )}
            </Card>

            {/* 카테고리 및 강사 정보 */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Card title="카테고리 정보">
                <div className="space-y-4">
                  <InfoItem label="카테고리명" value={course.category?.title || '-'} />
                  <InfoItem
                    label="썸네일"
                    value={
                      course.category?.thumbnailUrl ? (
                        <img
                          src={course.category.thumbnailUrl}
                          alt="Category Thumbnail"
                          className="h-20 w-20 rounded object-cover"
                        />
                      ) : (
                        '-'
                      )
                    }
                  />
                </div>
              </Card>

              <Card title="강사 정보">
                <div className="space-y-4">
                  <InfoItem label="강사명" value={course.instructor?.name || '-'} />
                  <InfoItem label="이메일" value={course.instructor?.email || '-'} />
                  <InfoItem label="직업" value={course.instructor?.occupation || '-'} />
                  <InfoItem
                    label="프로필 이미지"
                    value={(() => {
                      const imageUrl = getImageUrl(course.instructor?.imageUrl);
                      if (imageUrl && !instructorImageError) {
                        return (
                          <img
                            src={imageUrl}
                            alt={course.instructor?.name || 'Instructor Profile'}
                            className="h-20 w-20 rounded-full border border-slate-200 object-cover"
                            onError={(e) => {
                              console.error('강사 프로필 이미지 로드 실패:', {
                                originalUrl: course.instructor?.imageUrl,
                                resolvedUrl: imageUrl,
                                error: e,
                              });
                              setInstructorImageError(true);
                            }}
                            onLoad={() => {
                              console.log('강사 프로필 이미지 로드 성공:', {
                                originalUrl: course.instructor?.imageUrl,
                                resolvedUrl: imageUrl,
                              });
                            }}
                          />
                        );
                      } else if (course.instructor?.name) {
                        return (
                          <div className="flex h-20 w-20 items-center justify-center rounded-full border border-slate-200 bg-indigo-100 text-xl font-medium text-indigo-600">
                            {course.instructor.name.charAt(0)}
                          </div>
                        );
                      } else {
                        return '-';
                      }
                    })()}
                  />
                </div>
              </Card>
            </div>

            {/* 커리큘럼 (챕터 및 레슨) */}
            <Card
              title="커리큘럼"
              actions={
                <div className="flex gap-2">
                  {isReordering ? (
                    <>
                      <Button variant="outline" size="sm" onClick={handleCancelReordering}>
                        취소
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleCompleteReordering}>
                        순서 변경 완료
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setIsReordering(true)}>
                      순서 변경
                    </Button>
                  )}
                  <Button size="sm" onClick={handleAddChapter} leftIcon={<HiPlus />} disabled={isReordering}>
                    챕터 추가
                  </Button>
                </div>
              }
              className={isReordering ? 'ring-opacity-50 bg-indigo-50/30 ring-2 ring-indigo-500' : ''}>
              {isReordering && (
                <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                  <p className="text-sm font-medium text-indigo-800">
                    📌 순서 변경 모드: 드래그 핸들(☰)을 사용하여 챕터와 레슨의 순서를 변경할 수 있습니다.
                  </p>
                </div>
              )}
              <DragDropContext
                onDragEnd={handleDragEnd}
                onBeforeCapture={() => {
                  // 드래그 시작 전에 draggable이 준비되었는지 확인
                  if (!isReordering) {
                    return;
                  }
                }}>
                {chapters.length > 0 ? (
                  <Droppable
                    droppableId="chapters"
                    type="CHAPTER"
                    isDropDisabled={false}
                    isCombineEnabled={false}
                    ignoreContainerClipping={true}>
                    {(provided: DroppableProvided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-6">
                        {chapters.map((chapter, index) => (
                          <Draggable key={`chapter-${chapter.id}`} draggableId={`chapter-${chapter.id}`} index={index}>
                            {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`mb-6 ${snapshot.isDragging ? 'z-10 shadow-lg' : ''}`}>
                                <Card
                                  noPadding
                                  className={`transition-colors ${
                                    snapshot.isDragging
                                      ? 'border-2 border-indigo-400 bg-indigo-100 shadow-md'
                                      : isReordering
                                        ? 'border-2 border-indigo-300 bg-indigo-50'
                                        : ''
                                  }`}
                                  title={
                                    <div className="flex items-center gap-2">
                                      <div
                                        {...provided.dragHandleProps}
                                        className={`p-1 ${isReordering ? 'cursor-grab text-indigo-600 hover:text-indigo-700 active:cursor-grabbing' : 'cursor-default opacity-0'}`}>
                                        <MdDragIndicator className="text-xl" />
                                      </div>
                                      <span>{chapter.title}</span>
                                      <Badge variant="neutral" size="sm">
                                        {chapter.lessons.length} 레슨
                                      </Badge>
                                    </div>
                                  }
                                  actions={
                                    <div className="flex items-center gap-2">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleEditChapter(chapter.id, chapter.title)}
                                        leftIcon={<HiPencil />}
                                        disabled={isReordering}>
                                        수정
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                        onClick={() => handleDeleteClick('chapter', chapter.id, chapter.title)}
                                        leftIcon={<HiTrash />}
                                        disabled={isReordering}>
                                        삭제
                                      </Button>
                                    </div>
                                  }>
                                  <Droppable
                                    droppableId={`chapter-${chapter.id}`}
                                    type="LESSON"
                                    isDropDisabled={false}
                                    isCombineEnabled={false}
                                    ignoreContainerClipping={true}>
                                    {(provided: DroppableProvided) => (
                                      <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="divide-y divide-slate-100">
                                        {chapter.lessons.length === 0 ? (
                                          <div className="p-8 text-center">
                                            <p className="mb-4 text-sm text-slate-500">등록된 레슨이 없습니다.</p>
                                            <Button
                                              size="sm"
                                              variant="secondary"
                                              onClick={() =>
                                                navigate(`/dashboard/courses/${id}/chapters/${chapter.id}/lessons/new`)
                                              }
                                              leftIcon={<HiPlus />}
                                              disabled={isReordering}>
                                              레슨 추가
                                            </Button>
                                          </div>
                                        ) : (
                                          <>
                                            {chapter.lessons.map((lesson, lessonIndex) => (
                                              <Draggable
                                                key={`lesson-${lesson.id}`}
                                                draggableId={`lesson-${lesson.id}`}
                                                index={lessonIndex}>
                                                {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                                  <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    className={`group flex items-center justify-between rounded-lg p-3 transition-colors ${
                                                      snapshot.isDragging
                                                        ? 'ring-opacity-50 z-10 border-2 border-indigo-400 bg-indigo-100 shadow-lg ring-2 ring-indigo-500'
                                                        : isReordering
                                                          ? 'border-2 border-indigo-300 bg-indigo-50 hover:border-indigo-400 hover:bg-indigo-100'
                                                          : 'cursor-pointer border border-slate-200 bg-white hover:border-indigo-300 hover:bg-slate-50'
                                                    }`}
                                                    onClick={(e) => {
                                                      // 순서 변경 모드일 때는 클릭 이벤트 비활성화
                                                      if (isReordering) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        return;
                                                      }
                                                      // 버튼 클릭 시 이벤트 버블링 방지
                                                      if ((e.target as HTMLElement).closest('button')) return;
                                                      navigate(`/dashboard/lessons/${lesson.id}`);
                                                    }}>
                                                    <div className="flex items-center gap-3">
                                                      <div
                                                        {...provided.dragHandleProps}
                                                        className={`p-1 ${isReordering ? 'cursor-grab text-indigo-600 hover:text-indigo-700 active:cursor-grabbing' : 'cursor-default opacity-0'}`}>
                                                        <MdDragIndicator className="text-xl" />
                                                      </div>
                                                      <div className="flex flex-col">
                                                        <span className="text-sm font-medium text-slate-800">
                                                          {lesson.title}
                                                        </span>
                                                        <div className="mt-1 flex items-center gap-2">
                                                          <Badge variant="neutral" size="sm">
                                                            {lesson.kind}
                                                          </Badge>
                                                          {lesson.duration && (
                                                            <span className="text-xs text-slate-500">
                                                              {lesson.duration}
                                                            </span>
                                                          )}
                                                        </div>
                                                      </div>
                                                    </div>
                                                    <div
                                                      className={`flex items-center gap-2 transition-opacity ${isReordering ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}`}>
                                                      <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() =>
                                                          navigate(
                                                            `/dashboard/courses/${id}/chapters/${chapter.id}/lessons/${lesson.id}/edit`,
                                                          )
                                                        }
                                                        leftIcon={<HiPencil />}
                                                        disabled={isReordering}>
                                                        수정
                                                      </Button>
                                                      <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                                        onClick={() =>
                                                          handleDeleteClick(
                                                            'lesson',
                                                            lesson.id,
                                                            lesson.title,
                                                            chapter.id,
                                                          )
                                                        }
                                                        leftIcon={<HiTrash />}
                                                        disabled={isReordering}>
                                                        삭제
                                                      </Button>
                                                    </div>
                                                  </div>
                                                )}
                                              </Draggable>
                                            ))}
                                            <div className="border-t border-slate-200 p-4 text-center">
                                              <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() =>
                                                  navigate(
                                                    `/dashboard/courses/${id}/chapters/${chapter.id}/lessons/new`,
                                                  )
                                                }
                                                leftIcon={<HiPlus />}
                                                disabled={isReordering}>
                                                레슨 추가
                                              </Button>
                                            </div>
                                          </>
                                        )}
                                        {provided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-sm text-slate-500">등록된 챕터가 없습니다.</p>
                  </div>
                )}
              </DragDropContext>
            </Card>

            {/* 수강자 목록 카드 */}
            <Card title={`수강생 목록 (${enrollmentsData?.meta.total || 0}명)`} noPadding>
              <div className="flex items-center gap-4 px-6 py-1">
                <SelectFilter
                  value={filterKind}
                  onChange={handleFilterKindChange}
                  options={[
                    { value: ENROLLMENT_KIND.PURCHASE, label: '구매' },
                    { value: ENROLLMENT_KIND.GRANT, label: '부여' },
                  ]}
                  label="종류"
                  id="filterKindSelect"
                />
                <DateRangeFilter
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={(value) => handleDateChange('startDate', value)}
                  onEndDateChange={(value) => handleDateChange('endDate', value)}
                  startDateId="startDateInput"
                  endDateId="endDateInput"
                  endDateAriaLabel="종료 날짜"
                />
              </div>
              {isLoadingEnrollments ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : enrollmentsData && enrollmentsData.data.length > 0 ? (
                <div>
                  <table className="w-full">
                    <thead className="border-b border-slate-200 bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase"></th>
                        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase">
                          계정 ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase">
                          상태
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase">
                          종류
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase">
                          구매날짜
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase">
                          수강 시작일
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {enrollmentsData.data.map((enrollment) => (
                        <tr
                          key={enrollment.id}
                          onClick={() =>
                            navigate(`/dashboard/user/${enrollment.accountId}/enrollment/${enrollment.id}`)
                          }
                          className="cursor-pointer transition-colors hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-900">{enrollment.id}</td>
                          <td className="px-4 py-3 text-sm text-slate-900">
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                await navigate(`/dashboard/users/${enrollment.accountId}`);
                              }}
                              className="text-indigo-600 hover:text-indigo-800 hover:underline">
                              {enrollment.accountId}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Badge variant="neutral">{enrollment.state}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Badge variant={enrollment.kind === ENROLLMENT_KIND.PURCHASE ? 'primary' : 'success'}>
                              {enrollment.kind === ENROLLMENT_KIND.PURCHASE ? '구매' : '부여'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {new Date(enrollment.createdAt).toLocaleDateString('ko-KR')}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {enrollment.enrolledAt ? new Date(enrollment.enrolledAt).toLocaleDateString('ko-KR') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* 페이지네이션 */}
                  {enrollmentsData.meta.totalPages > 1 && (
                    <div className="border-t border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-slate-600">
                          {enrollmentsData.meta.total}개 중 {(enrollmentsPage - 1) * enrollmentsLimit + 1}-
                          {Math.min(enrollmentsPage * enrollmentsLimit, enrollmentsData.meta.total)}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEnrollmentsPage((p) => Math.max(1, p - 1))}
                            disabled={enrollmentsPage === 1}>
                            이전
                          </Button>
                          <div className="flex items-center gap-2">
                            {Array.from({ length: enrollmentsData.meta.totalPages }, (_, i) => i + 1)
                              .filter(
                                (p) =>
                                  p === 1 ||
                                  p === enrollmentsData.meta.totalPages ||
                                  Math.abs(p - enrollmentsPage) <= 2,
                              )
                              .map((page, idx, arr) => (
                                <React.Fragment key={page}>
                                  {idx > 0 && arr[idx - 1] !== page - 1 && <span className="text-slate-400">...</span>}
                                  <Button
                                    variant={page === enrollmentsPage ? 'primary' : 'outline'}
                                    size="sm"
                                    onClick={() => setEnrollmentsPage(page)}>
                                    {page}
                                  </Button>
                                </React.Fragment>
                              ))}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEnrollmentsPage((p) => Math.min(enrollmentsData.meta.totalPages, p + 1))}
                            disabled={enrollmentsPage === enrollmentsData.meta.totalPages}>
                            다음
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-4 py-8 text-center text-sm text-slate-400">수강자가 없습니다.</div>
              )}
            </Card>
          </div>
        </>
      )}
    </DetailPageLayout>
  );
};

export default CourseDetail;
